/* eslint-disable react-hooks/exhaustive-deps */
import {FC, createContext, useContext, useEffect, useState} from 'react'
import {WithChildren} from '../../helpers'
import {useLocation, useParams} from 'react-router-dom'
import Layout from './Layout'
import {useAuth} from 'app/modules/auth'
import useAuditContext from 'app/Hooks/useAuditContext'

export interface PageLink {
  title: string
  path: string
  isActive: boolean
  isSeparator?: boolean
}

export interface PageDataContextModel {
  pageTitle?: string
  setPageTitle: (_title: string) => void
  pageDescription?: string
  setPageDescription: (_description: string) => void
  pageBreadcrumbs?: Array<PageLink>
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void
}

const PageDataContext = createContext<PageDataContextModel>({
  setPageTitle: (_title: string) => {},
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {},
  setPageDescription: (_description: string) => {},
})

const PageDataProvider: FC<WithChildren> = ({children}) => {
  const [pageTitle, setPageTitle] = useState<string>('')
  const [pageDescription, setPageDescription] = useState<string>('')
  const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([])

  const value: PageDataContextModel = {
    pageTitle,
    setPageTitle,
    pageDescription,
    setPageDescription,
    pageBreadcrumbs,
    setPageBreadcrumbs,
  }
  return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>
}

function usePageData() {
  return useContext(PageDataContext)
}

type Props = {
  description?: string
  breadcrumbs?: Array<PageLink>
  title?: string
  icon?: string
  desc?: string
}

const tabs = [
  {
    title: 'Risks',
    slug: 'risk-management',
    href: '/risk-management',
  },
  {
    title: 'Risk Library',
    slug: 'library',
    href: '/risk-management/library',
  },
]

const RiskPageTitle: FC<Props & WithChildren> = ({
  children,
  description,
  breadcrumbs,
  title,
  icon,
  desc,
}) => {
  const {setPageTitle, setPageDescription, setPageBreadcrumbs} = usePageData()
  const location = useLocation()
  const {currentUser} = useAuth()
  const [activeTab, setActiveTab] = useState(
    location.pathname.split('/').at(-1) === '' ? tabs[0].slug : location.pathname.split('/').at(-1)
  )
  const {id} = useParams()
  const {currentAuditContext} = useAuditContext()

  const filteredTabs = currentUser?.role === 'Auditor'
    ? tabs.filter(tab => tab.slug !== 'library')
    : currentAuditContext
    ? tabs.filter(tab => tab.slug !== 'library')
    : tabs

  useEffect(() => {
    if (children) {
      setPageTitle(children.toString())
    }
    return () => {
      setPageTitle('')
    }
  }, [children])

  useEffect(() => {
    if (description) {
      setPageDescription(description)
    }
    return () => {
      setPageDescription('')
    }
  }, [description])

  useEffect(() => {
    if (breadcrumbs) {
      setPageBreadcrumbs(breadcrumbs)
    }
    return () => {
      setPageBreadcrumbs([])
    }
  }, [breadcrumbs])

  useEffect(() => {
    if (location.pathname.includes('library')) {
      setActiveTab('library')
    } else {
      setActiveTab('risk-management')
    }
  }, [location.pathname, id])
  return (
    <>
      {icon ? (
        <Layout icon={icon} title={title} desc={desc} tabs={filteredTabs} activeTab={activeTab} />
      ) : null}
    </>
  )
}

const PageDescription: FC<WithChildren> = ({children}) => {
  const {setPageDescription} = usePageData()
  useEffect(() => {
    if (children) {
      setPageDescription(children.toString())
    }
    return () => {
      setPageDescription('')
    }
  }, [children])
  return <></>
}

export {PageDescription, RiskPageTitle, PageDataProvider, usePageData}